import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { webAuth } from '../api/client';

const passwordRules = [
  { test: (p) => p.length >= 8,            label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p),          label: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p),          label: 'One lowercase letter' },
  { test: (p) => /[0-9]/.test(p),          label: 'One number' },
  { test: (p) => /[^A-Za-z0-9]/.test(p),  label: 'One special character' },
];

export default function CreateAccountScreen() {
  const [form, setForm] = useState({ email: '', username: '', password: '', confirmPassword: '', stationNumber: '', licenseKey: '' });
  const [errors, setErrors]                   = useState({});
  const [usernameStatus, setUsernameStatus]   = useState(null); // 'checking' | 'available' | 'taken'
  const [loading, setLoading]                 = useState(false);
  const [success, setSuccess]                 = useState(false);
  const usernameTimer = useRef(null);
  const navigate = useNavigate();

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  // Live username availability check
  useEffect(() => {
    if (form.username.length < 3) { setUsernameStatus(null); return; }
    if (usernameTimer.current) clearTimeout(usernameTimer.current);
    setUsernameStatus('checking');
    usernameTimer.current = setTimeout(async () => {
      try {
        const res = await webAuth.checkUsername(form.username);
        setUsernameStatus(res.data.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus(null);
      }
    }, 500);
  }, [form.username]);

  const validate = () => {
    const e = {};
    if (!form.email) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email.';
    if (!form.username) e.username = 'Username is required.';
    else if (!/^[a-zA-Z0-9_]{3,30}$/.test(form.username)) e.username = 'Letters, numbers, underscores only (3-30 chars).';
    else if (usernameStatus === 'taken') e.username = 'Username already taken.';
    const failedRules = passwordRules.filter(r => !r.test(form.password));
    if (failedRules.length > 0) e.password = 'Password does not meet requirements.';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
    if (!form.stationNumber) e.stationNumber = 'Station number is required.';
    if (!form.licenseKey) e.licenseKey = 'License key is required.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      await webAuth.register(form);
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed. Please try again.';
      setErrors({ general: msg });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={s.container}>
        <div style={s.card}>
          <div style={s.successIcon}>✉️</div>
          <h2 style={s.successTitle}>Check your email</h2>
          <p style={s.successText}>
            We sent a verification link to <strong>{form.email}</strong>.
            Click the link to activate your account.
          </p>
          <button style={s.button} onClick={() => navigate('/login')}>Back to Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={s.card}>
        <h1 style={s.title}>Create Account</h1>
        <p style={s.subtitle}>Set up your MVIRS web access</p>

        {errors.general && <div style={s.error}>{errors.general}</div>}

        <form onSubmit={handleSubmit}>

          {/* Email */}
          <div style={s.field}>
            <label style={s.label}>Email</label>
            <input style={s.input} type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
            {errors.email && <div style={s.fieldError}>{errors.email}</div>}
          </div>

          {/* Username */}
          <div style={s.field}>
            <label style={s.label}>Username</label>
            <div style={{ position: 'relative' }}>
              <input style={s.input} type="text" value={form.username} onChange={set('username')} placeholder="Choose a username" autoCapitalize="none" autoCorrect="off" />
              {usernameStatus === 'checking' && <span style={s.usernameHint}>Checking...</span>}
              {usernameStatus === 'available' && <span style={{ ...s.usernameHint, color: '#34C759' }}>✓ Available</span>}
              {usernameStatus === 'taken' && <span style={{ ...s.usernameHint, color: '#cc0000' }}>✗ Taken</span>}
            </div>
            {errors.username && <div style={s.fieldError}>{errors.username}</div>}
          </div>

          {/* Password */}
          <div style={s.field}>
            <label style={s.label}>Password</label>
            <input style={s.input} type="password" value={form.password} onChange={set('password')} placeholder="Create a password" />
            <div style={s.rules}>
              {passwordRules.map(r => (
                <div key={r.label} style={{ ...s.rule, color: r.test(form.password) ? '#34C759' : '#999' }}>
                  {r.test(form.password) ? '✓' : '○'} {r.label}
                </div>
              ))}
            </div>
            {errors.password && <div style={s.fieldError}>{errors.password}</div>}
          </div>

          {/* Confirm Password */}
          <div style={s.field}>
            <label style={s.label}>Confirm Password</label>
            <input style={s.input} type="password" value={form.confirmPassword} onChange={set('confirmPassword')} placeholder="Re-enter your password" />
            {errors.confirmPassword && <div style={s.fieldError}>{errors.confirmPassword}</div>}
          </div>

          <div style={s.divider} />

          {/* Station Number */}
          <div style={s.field}>
            <label style={s.label}>Station Number</label>
            <input style={s.input} type="text" value={form.stationNumber} onChange={set('stationNumber')} placeholder="Enter your MVIRS station number" autoCapitalize="none" autoCorrect="off" />
            <div style={s.hint}>Your MVIRS station number (e.g. 4046)</div>
            {errors.stationNumber && <div style={s.fieldError}>{errors.stationNumber}</div>}
          </div>

          {/* License Key */}
          <div style={s.field}>
            <label style={s.label}>License Key</label>
            <input style={s.input} type="text" value={form.licenseKey} onChange={set('licenseKey')} placeholder="Enter your MVIRS license key" autoCapitalize="none" autoCorrect="off" />
            <div style={s.hint}>Found in MVIRS under Help → About. Contact your administrator if you don't have it.</div>
            {errors.licenseKey && <div style={s.fieldError}>{errors.licenseKey}</div>}
          </div>

          <button style={loading ? s.buttonDisabled : s.button} type="submit" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div style={s.divider} />
        <p style={s.bottomText}>Already have an account? <Link to="/login" style={s.link}>Sign In</Link></p>
      </div>
    </div>
  );
}

const s = {
  container:      { minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card:           { backgroundColor: '#fff', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%', maxWidth: '440px' },
  title:          { textAlign: 'center', fontSize: '28px', fontWeight: 'bold', color: '#1a1a2e', margin: '0 0 8px 0' },
  subtitle:       { textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '28px' },
  field:          { marginBottom: '18px' },
  label:          { display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#333' },
  input:          { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px', boxSizing: 'border-box', outline: 'none' },
  fieldError:     { color: '#cc0000', fontSize: '13px', marginTop: 4 },
  error:          { backgroundColor: '#ffe0e0', color: '#cc0000', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', textAlign: 'center' },
  usernameHint:   { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#999', pointerEvents: 'none' },
  rules:          { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 },
  rule:           { fontSize: 13, transition: 'color 0.2s' },
  hint:           { fontSize: 12, color: '#999', marginTop: 4 },
  button:         { width: '100%', padding: '14px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
  buttonDisabled: { width: '100%', padding: '14px', backgroundColor: '#999', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'not-allowed', marginTop: '8px' },
  divider:        { borderTop: '1px solid #eee', margin: '24px 0' },
  bottomText:     { textAlign: 'center', color: '#666', fontSize: '14px' },
  link:           { color: '#007AFF', textDecoration: 'none' },
  successIcon:    { textAlign: 'center', fontSize: 48, marginBottom: 16 },
  successTitle:   { textAlign: 'center', fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 },
  successText:    { textAlign: 'center', color: '#555', fontSize: 15, lineHeight: 1.6, marginBottom: 24 },
};